import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { Dropdown } from 'react-bootstrap'
import { isWebUri } from 'valid-url'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { ExternalLink } from 'src/components/Reusable/Text/Link'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { FileSelector } from 'src/components/Reusable/Forms/FileSelector/FileSelector'
import { Spinner } from 'src/Throbber'
import { TestIds } from 'src/Utils/testIds'
import { P } from 'src/components/Reusable/Text/Text.styles'
import palette from 'src/designSystem/variables/palette'
import { useSetActiveModal } from 'src/Modal/ModalStateContext'
import {
  useHasReceptor,
  useReceptor,
  useReceptors,
} from 'src/pages/Receptors/receptors.hooks'
import { documentSourceLabel } from 'src/pages/Receptors/documentSourceLabel.helpers'
import { SharepointItem } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerModal.types'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import { ExtendedDocumentObject } from 'src/lib/Linkers/Linkers.constants'
import { useGooglePicker } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { useIsValidSharepointUrl } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerModal.hooks'
import { useSharepointLinkPaste } from 'src/context/Gatekeeper'
import { ConfluenceNode } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerModal.types'
import {
  DocumentNode,
  useDirectPickerSelection,
  useDocumentPickerIfAvailable,
} from 'src/components/DocumentPicker'
import { documentSourceLabel as connectionLabel } from 'src/pages/Receptors/documentSourceLabel.helpers'
import formatDate from '../../../../../Utils/formatDate'
import { ReactComponent as TextIcon } from '../../../../../components/Icons/assets/evidence-text-icon.svg'
import { ReactComponent as LinkIcon } from '../../../../../components/Icons/assets/permalink-icon.svg'
import { TextButton } from '../../../../Reusable/Buttons/TextButton'
import {
  DerivedTextArea,
  TextInput,
  TextInputProps,
} from '../../../../Reusable/Inputs'
import { StyledTextInput } from '../../../../Reusable/Inputs/TextInput/styles'
import { TypeButton } from '../../../FileType/TypeSelector.styles'
import { MIME_TYPE } from '../../../../../Utils/globalEnums'
import { useFileTypeIcon } from '../../../FileType/useFileTypeIcon'
import { ModalFormId } from '../../../ModalForm'
import {
  AddEvidenceError,
  CaptionLabel,
  ConfluenceIcon,
  EvidenceDetailsContainer,
  FilesInfoContainer,
  FilesInfoContainerRow,
  FilesLoadingContainer,
  Label,
  UploadInfo,
  UploadInfoSection,
  SharePointIcon,
  SharePointSmallIcon,
  GoogleDriveIcon,
  GoogleDriveSmallIcon,
  ConfluenceSmallIcon,
  NotionIcon,
  NotionSmallIcon,
  LinkerCard,
  SelectionCardWrapper,
  OutlinedDropdownMenu,
} from './AddEvidenceForm.styles'
import {
  AddEvidenceErrorType,
  EvidenceFormData,
  EvidenceType,
} from './AddEvidenceForm.constants'

type CaptionTextInputProps = TextInputProps & {
  createCaption: (captionString: string) => void
  isError: (field: string, error: boolean) => void
  captionError: string
}

export const CaptionTextInput = ({
  as,
  createCaption,
  name,
  initVal = '',
  placeholder,
  form,
  label,
  captionError,
  isError,
  ...styleProps
}: CaptionTextInputProps): JSX.Element => {
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (!captionError) {
      return
    }
    setShowError(true)
  }, [captionError])

  const onChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    const newVal = e.target.value
    createCaption(newVal)
    if (!newVal.length) {
      setShowError(true)
      isError('caption', true)
      return
    }
    isError(AddEvidenceErrorType.CAPTION, false)
    setShowError(false)
  }

  const onKeyDown: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (e.key !== 'Enter') {
      return
    }
    e.preventDefault()
  }

  const inputProps = {
    as,
    name,
    value: initVal,
    placeholder,
    onChange,
    onKeyDown,
    showError,
  }

  const labelProps = useMemo<
    Omit<typeof styleProps, 'onKeyDown' | 'onKeyUp'>
  >(() => {
    return {
      ...styleProps,
      form,
    }
  }, [styleProps, form])

  return (
    <>
      {label ? (
        <CaptionLabel form={form} {...labelProps}>
          <p>{label}</p>
          <StyledTextInput {...inputProps} />
        </CaptionLabel>
      ) : (
        <StyledTextInput {...{ ...inputProps, ...styleProps }} />
      )}
    </>
  )
}

type EvidenceFileProps = {
  formData: EvidenceFormData
}

export const EvidenceFile = ({ formData }: EvidenceFileProps): JSX.Element => {
  const { mime, body } = formData
  const FileTypeIcon = useFileTypeIcon({ mime })

  if (!(body instanceof Blob)) return <></>

  return (
    <>
      <FilesInfoContainerRow>
        <FileTypeIcon width="24px" height="24px" />
        <span className="filename">{body.name}</span>
        <span className="last-modified">
          Modified on {formatDate(body.lastModified)}
        </span>
      </FilesInfoContainerRow>
    </>
  )
}

export const DocumentManagementFile = (file: SharepointItem): JSX.Element => {
  return (
    <>
      <FilesInfoContainerRow>
        <SharePointSmallIcon />
        <span className="filename">{file.name}</span>
        <span className="last-modified">
          {(file.size / 1024).toFixed(2)} KB
        </span>
      </FilesInfoContainerRow>
    </>
  )
}

export const GoogleDocumentManagementFile = (
  file: ExtendedDocumentObject,
): JSX.Element => {
  return (
    <>
      <FilesInfoContainerRow>
        <GoogleDriveSmallIcon />
        <span className="filename">{file.name}</span>
        <span className="last-modified">
          {((file.sizeBytes || 0) / 1024).toFixed(2)} KB
        </span>
      </FilesInfoContainerRow>
    </>
  )
}

/** A document picked in the document picker from a connection without a
 * legacy picker context (Notion). */
export const PickedDocumentFile = (node: DocumentNode): JSX.Element => {
  return (
    <>
      <FilesInfoContainerRow>
        <NotionSmallIcon />
        <span className="filename">{node.title || node.id}</span>
        <span className="last-modified">
          {node.modifiedAt ? formatDate(node.modifiedAt) : ''}
        </span>
      </FilesInfoContainerRow>
    </>
  )
}

export const ConfluenceDocumentManagementFile = (
  file: ConfluenceNode,
): JSX.Element => {
  return (
    <>
      <FilesInfoContainerRow>
        <ConfluenceSmallIcon />
        <span className="filename">{file.fileName}</span>
        <span className="last-modified"> {file.relevantDate}</span>
      </FilesInfoContainerRow>
    </>
  )
}

type EvidenceFilesProps = {
  formDataList: EvidenceFormData[]
  type: EvidenceType
}

export const EvidenceFiles = ({
  formDataList,
  type,
}: EvidenceFilesProps): JSX.Element => {
  const { filesSelected: sharepointFilesSelected } =
    useSharepointFilePickerContext()
  const { filesSelected: gdriveFilesSelected } = useGoogleDriveContext()
  const { filesSelected: confluenceFilesSelected } =
    useConfluenceFilePickerContext()
  const { nodes: pickedNodes } = useDirectPickerSelection('Evidence')
  const noEvidenceFiles =
    type !== (EvidenceType.FILE || formDataList.length < 1) &&
    sharepointFilesSelected.length < 1 &&
    (gdriveFilesSelected || []).length < 1 &&
    (confluenceFilesSelected || []).length < 1 &&
    pickedNodes.length < 1
  return (
    <>
      {noEvidenceFiles ? (
        <></>
      ) : (
        <FilesInfoContainer>
          {type === EvidenceType.FILE &&
            formDataList.map((formData, i) => (
              <EvidenceFile key={`evidence-file-${i}`} formData={formData} />
            ))}
          {sharepointFilesSelected.map((file) => (
            <DocumentManagementFile
              key={`evidence-file-${file.id}`}
              {...file}
            />
          ))}
          {(gdriveFilesSelected || []).map((file) => (
            <GoogleDocumentManagementFile
              key={`gdrive-evidence-file-${file.id}`}
              {...file}
            />
          ))}
          {(confluenceFilesSelected || []).map((file) => (
            <ConfluenceDocumentManagementFile
              key={`confluence-evidence-file-${file.id}`}
              {...file}
            />
          ))}
          {pickedNodes.map((node) => (
            <PickedDocumentFile
              key={`picked-evidence-file-${node.id}`}
              {...node}
            />
          ))}
        </FilesInfoContainer>
      )}
    </>
  )
}

type EvidenceTextProps = {
  formData: EvidenceFormData
  setFormData: Dispatch<SetStateAction<EvidenceFormData>>
  formId: string
  type: EvidenceType
}

export const EvidenceText = ({
  formData,
  setFormData,
  formId,
  type,
}: EvidenceTextProps): JSX.Element => {
  const useSPLinkPaste = useSharepointLinkPaste()
  const isSharepoinntUrlValid = useIsValidSharepointUrl()
  const { data: HasSPReceptor } = useHasReceptor('trr-sharepoint', true)
  const hasSharepointReceptor = useMemo(
    () => HasSPReceptor?.getHasReceptor() || false,
    [HasSPReceptor],
  )
  const { data: receptor } = useReceptor({
    modelId: 'trr-sharepoint',
    shouldFetch: hasSharepointReceptor,
  })

  const spCredentials = useMemo(() => {
    return JSON.parse(receptor?.getCredentials() || '{}')
  }, [receptor])

  const isValid = useMemo(() => {
    if (type === EvidenceType.LINK) {
      return (value: string) => !!isWebUri(value)
    } else if (type === EvidenceType.SHAREPOINTLINK && useSPLinkPaste) {
      return (value: string) => isSharepoinntUrlValid(value)
    }
    return undefined
  }, [type, useSPLinkPaste, isSharepoinntUrlValid])

  return (
    <>
      {(type === EvidenceType.TEXT || type === EvidenceType.LINK) && (
        <DerivedTextArea
          required
          name="body"
          initVal={formData.body as string}
          placeholder={`Add ${type} Content`}
          form={formId}
          setFormData={
            setFormData as Dispatch<SetStateAction<{ [key: string]: string }>>
          }
          isValid={isValid}
          errorMessage={
            type === EvidenceType.LINK
              ? 'Please enter a valid url that begins with ' +
                `"https://" ` +
                'or ' +
                `"http://"`
              : undefined
          }
        />
      )}
      {type === EvidenceType.SHAREPOINTLINK && useSPLinkPaste && (
        <TextInput
          required
          name="body"
          initVal={formData.body as string}
          placeholder="Please enter a valid SharePoint link"
          form={formId}
          onChangeCb={(e) => setFormData((prev) => ({ ...prev, body: e }))}
          isValid={isValid}
          errorMessage={
            'Please enter a valid SharePoint url that begins with ' +
            (spCredentials?.baseurl || 'https://')
          }
        />
      )}
    </>
  )
}

type EvidenceTypeSelectorProps = {
  updateEvidence: (mime: MIME_TYPE, type: EvidenceType) => void
  updateEvidenceFiles: (files: EvidenceFormData[], type: EvidenceType) => void
  type: EvidenceType
  updateUploadSize: (totalSizeInMB: number) => void
  modalId: ModalFormId | undefined
}

export function EvidenceTypeSelector({
  updateEvidence,
  updateEvidenceFiles,
  type,
  updateUploadSize,
  modalId,
}: EvidenceTypeSelectorProps): JSX.Element {
  const useSPLinkPaste = useSharepointLinkPaste()
  const openPicker = useGooglePicker()
  const {
    setPickerConfig,
    pickerConfig,
    filesSelected: gdriveFilesSelected,
    setFilesSelected: setGDriveFileSelected,
  } = useGoogleDriveContext()
  const {
    filesSelected: sharepointFilesSelected,
    setFilesSelected: setSharepointFilesSelected,
    setReceptor: setSharepointReceptor,
  } = useSharepointFilePickerContext()
  const {
    filesSelected: confluenceFilesSelected,
    setFilesSelected: setConfluenceFilesSelected,
    setReceptor: setConfluenceReceptor,
  } = useConfluenceFilePickerContext()
  const openSharepointModal = useSetActiveModal(
    ModalFormId.SHAREPOINT_FILE_PICKER,
  )
  const openConfluenceModal = useSetActiveModal(
    ModalFormId.CONFLUENCE_FILE_PICKER,
  )
  // Notion has no legacy picker: its pages are picked in the document
  // picker and linked from its selection on submit.
  const documentPicker = useDocumentPickerIfAvailable()
  const { nodes: pickedNodes, clear: clearPickedNodes } =
    useDirectPickerSelection('Evidence')
  const { data: HasNotionReceptor } = useHasReceptor('trr-notion', true)
  const hasNotionReceptor = useMemo(
    () => HasNotionReceptor?.getHasReceptor() || false,
    [HasNotionReceptor],
  )
  const { data: notionReceptors } = useReceptors(
    'trr-notion',
    hasNotionReceptor,
  )
  const enabledNotionReceptors = useMemo(
    () =>
      (notionReceptors?.getReceptorsList() || []).filter((r) =>
        r.getIsenabled(),
      ),
    [notionReceptors],
  )
  const openNotionPicker = (receptor: ReceptorRecord) => {
    if (!documentPicker) return
    documentPicker.openPicker({
      receptor,
      mode: 'files',
      source: 'Evidence',
      parentModalId: modalId ?? ModalFormId.ADD_EVIDENCE,
    })
  }

  const { data: HasSharepointReceptor } = useHasReceptor('trr-sharepoint', true)
  const hasSharepointReceptor = useMemo(
    () => HasSharepointReceptor?.getHasReceptor() || false,
    [HasSharepointReceptor],
  )
  const { data: spReceptors } = useReceptors(
    'trr-sharepoint',
    hasSharepointReceptor,
  )
  const isSharepointConnected = useMemo(() => {
    if (!spReceptors) return false
    return spReceptors
      .getReceptorsList()
      .some((receptor) => receptor.getIsenabled())
  }, [spReceptors])

  const { data: HasConfluenceReceptor } = useHasReceptor(
    'trr-confluence-dms',
    true,
  )

  const hasConfluenceReceptor = useMemo(
    () => HasConfluenceReceptor?.getHasReceptor() || false,
    [HasConfluenceReceptor],
  )

  const { data: confluenceReceptors } = useReceptors(
    'trr-confluence-dms',
    hasConfluenceReceptor,
  )

  const isConfluenceConnected = useMemo(() => {
    if (!confluenceReceptors) return false
    return confluenceReceptors
      .getReceptorsList()
      .some((receptor) => receptor.getIsenabled())
  }, [confluenceReceptors])

  const { data: HasGDriveReceptor } = useHasReceptor('trr-gdrive', true)
  const hasGDriveReceptor = useMemo(
    () => HasGDriveReceptor?.getHasReceptor() || false,
    [HasGDriveReceptor],
  )

  const { data: gdriveReceptors } = useReceptors(
    'trr-gdrive',
    hasGDriveReceptor,
  )

  const isGDriveConnected = useMemo(() => {
    if (!gdriveReceptors) return false
    return gdriveReceptors
      .getReceptorsList()
      .some((receptor) => receptor.getIsenabled())
  }, [gdriveReceptors])

  const showClear =
    type !== EvidenceType.NULL ||
    sharepointFilesSelected.length > 0 ||
    (gdriveFilesSelected || []).length > 0 ||
    (confluenceFilesSelected || []).length > 0 ||
    pickedNodes.length > 0

  const handleFileChange = (files: File[]) => {
    if (files.length === 0) return
    const totalSize = files
      ? Array.from(files).reduce((size, file) => {
          size += file.size
          return size
        }, 0)
      : 0
    const totalSizeInMB = Math.round(totalSize / 1000000)
    updateUploadSize(totalSizeInMB)
    const evidenceFormData = files.map((file) => ({
      mime: file.type,
      body: file,
    }))
    updateEvidenceFiles(evidenceFormData, EvidenceType.FILE)
  }

  const handleClear = () => {
    updateEvidence(MIME_TYPE.NULL, EvidenceType.NULL)
    updateEvidenceFiles([], EvidenceType.NULL)
    setSharepointFilesSelected([])
    setGDriveFileSelected?.([])
    setConfluenceFilesSelected([])
    clearPickedNodes()
  }

  const handleGoogleDriveSelection = async (receptor: ReceptorRecord) => {
    if (!pickerConfig) return
    // The document picker takes over as the active modal and hands back to
    // this one when it closes, so there is nothing to hide here. Hiding
    // would close the picker too when this form is shown by prop (the
    // request comment upload) and its hide clears every modal.
    const { invalidCredentials, exceptions } = await openPicker(
      pickerConfig,
      receptor.toObject(),
    )
    if (exceptions || invalidCredentials) {
      setPickerConfig(null)
    }
  }

  let alternateTypeOptions = [
    <TypeButton
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        updateEvidence(MIME_TYPE.TEXT_MARKDOWN, EvidenceType.TEXT)
      }}
      key="text"
    >
      <TextIcon />
      <span>Display Text</span>
    </TypeButton>,
    <TypeButton
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        updateEvidence(MIME_TYPE.TEXT_URI_LIST, EvidenceType.LINK)
      }}
      key="link"
    >
      <LinkIcon width="48px" height="48px" />
      <span>Paste Link</span>
    </TypeButton>,
  ]
  if (isGDriveConnected) {
    const enabledGDriveReceptors = (
      gdriveReceptors?.getReceptorsList() || []
    ).filter((f) => f.getIsenabled())

    if (enabledGDriveReceptors.length > 1) {
      alternateTypeOptions.push(
        <SelectionCardWrapper
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          style={{ height: '100%' }}
          key="gdrive-dropdown"
        >
          <LinkerCard>
            <GoogleDriveIcon />
            <span>Google Drive</span>
            <Dropdown>
              <Dropdown.Toggle as={OutlinedDropdownMenu} id="dropdown-basic">
                Select
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {enabledGDriveReceptors.map((receptor) => (
                  <Dropdown.Item
                    key={receptor.getId()}
                    onClick={async () => {
                      handleGoogleDriveSelection(receptor)
                    }}
                  >
                    {documentSourceLabel(receptor)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </LinkerCard>
        </SelectionCardWrapper>,
      )
    } else if (enabledGDriveReceptors.length === 1) {
      alternateTypeOptions.push(
        <TypeButton
          onClick={async (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            handleGoogleDriveSelection(enabledGDriveReceptors[0])
          }}
          key="google-drive"
        >
          <GoogleDriveIcon />
          <span>Google Drive</span>
        </TypeButton>,
      )
    }
  }

  if (isSharepointConnected) {
    const enabledSharepointReceptors = (
      spReceptors?.getReceptorsList() || []
    ).filter((f) => f.getIsenabled())

    if (enabledSharepointReceptors.length > 1) {
      alternateTypeOptions = [
        ...alternateTypeOptions,
        <SelectionCardWrapper
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          style={{ height: '100%' }}
          key="sharepoint-dropdown"
        >
          <LinkerCard>
            <SharePointIcon />
            <span>Sharepoint</span>
            <Dropdown>
              <Dropdown.Toggle as={OutlinedDropdownMenu} id="dropdown-basic">
                Select
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {enabledSharepointReceptors.map((receptor) => (
                  <Dropdown.Item
                    key={receptor.getId()}
                    onClick={() => {
                      if (!useSPLinkPaste) {
                        setSharepointReceptor(receptor)
                        openSharepointModal()
                      } else {
                        updateEvidence(
                          MIME_TYPE.TEXT_URI_LIST,
                          EvidenceType.SHAREPOINTLINK,
                        )
                      }
                    }}
                  >
                    {receptor.getTenantid()}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </LinkerCard>
        </SelectionCardWrapper>,
      ]
    } else if (enabledSharepointReceptors.length === 1) {
      alternateTypeOptions = [
        ...alternateTypeOptions,
        <TypeButton
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!useSPLinkPaste) {
              setSharepointReceptor(enabledSharepointReceptors[0])
              openSharepointModal()
            } else
              updateEvidence(
                MIME_TYPE.TEXT_URI_LIST,
                EvidenceType.SHAREPOINTLINK,
              )
          }}
          key="sharepoint"
        >
          <SharePointIcon />
          <span>Sharepoint</span>
        </TypeButton>,
      ]
    }
  }

  if (isConfluenceConnected) {
    const enabledConfluenceReceptors = (
      confluenceReceptors?.getReceptorsList() || []
    ).filter((f) => f.getIsenabled())

    if (enabledConfluenceReceptors.length > 1) {
      alternateTypeOptions = [
        ...alternateTypeOptions,
        <SelectionCardWrapper
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          style={{ height: '100%' }}
          key="confluence-dropdown"
        >
          <LinkerCard>
            <ConfluenceIcon />
            <span>Confluence</span>
            <Dropdown>
              <Dropdown.Toggle as={OutlinedDropdownMenu} id="dropdown-basic">
                Select
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {enabledConfluenceReceptors.map((receptor) => (
                  <Dropdown.Item
                    key={receptor.getId()}
                    onClick={() => {
                      setConfluenceReceptor(receptor)
                      openConfluenceModal()
                    }}
                  >
                    {receptor.getTenantid()}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </LinkerCard>
        </SelectionCardWrapper>,
      ]
    } else if (enabledConfluenceReceptors.length === 1) {
      alternateTypeOptions = [
        ...alternateTypeOptions,
        <TypeButton
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setConfluenceReceptor(enabledConfluenceReceptors[0])
            openConfluenceModal()
          }}
          key="confluence"
        >
          <ConfluenceIcon />
          <span>Confluence</span>
        </TypeButton>,
      ]
    }
  }

  if (documentPicker && enabledNotionReceptors.length > 1) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <SelectionCardWrapper
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        style={{ height: '100%' }}
        key="notion-dropdown"
      >
        <LinkerCard>
          <NotionIcon />
          <span>Notion</span>
          <Dropdown>
            <Dropdown.Toggle as={OutlinedDropdownMenu} id="dropdown-basic">
              Select
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {enabledNotionReceptors.map((receptor) => (
                <Dropdown.Item
                  key={receptor.getId()}
                  onClick={() => openNotionPicker(receptor)}
                >
                  {connectionLabel(receptor)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </LinkerCard>
      </SelectionCardWrapper>,
    ]
  } else if (documentPicker && enabledNotionReceptors.length === 1) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <TypeButton
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          openNotionPicker(enabledNotionReceptors[0])
        }}
        key="notion"
      >
        <NotionIcon />
        <span>Notion</span>
      </TypeButton>,
    ]
  }

  return (
    <>
      {showClear ? (
        <TextButton
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleClear()
          }}
        >
          Clear
        </TextButton>
      ) : (
        <FileSelector
          alternateTypeOptions={alternateTypeOptions}
          onChange={handleFileChange}
          isLoading={false}
          onClear={handleClear}
          isValid={true}
          errorMessage={''}
          allowMultiple
          modalId={modalId}
        />
      )}
    </>
  )
}

export const AddEvidenceFormDescription = (): JSX.Element => (
  <span>
    Best practice for providing manual evidence:&nbsp;
    <ExternalLink
      href={HUBSPOT.GOOD_MANUAL_EVIDENCE}
      text="Good Manual Evidence"
    />
  </span>
)

export const AddEvidenceCaption = ({
  numFiles,
  caption,
  error,
  isError,
  createCaption,
}: {
  numFiles: number
  caption: string
  error: string
  isError: () => boolean
  createCaption: (caption: string) => void
}): JSX.Element => {
  return (
    <>
      {numFiles > 1 ? (
        <P $fontSize={14} $mb={30}>
          Captions will automatically be set to the file names.
        </P>
      ) : (
        <CaptionTextInput
          label="Caption"
          name="caption"
          initVal={caption}
          captionError={error}
          isError={isError}
          placeholder="Add Caption for Evidence"
          form={ModalFormId.ADD_EVIDENCE}
          createCaption={createCaption}
        />
      )}
      <AddEvidenceError showError={!!error}>{error}</AddEvidenceError>
    </>
  )
}

export const FilesLoading = ({
  type,
  numFiles,
  uploadSize,
}: {
  type: EvidenceType
  numFiles: number
  uploadSize: number
}): JSX.Element => {
  return (
    <FilesLoadingContainer>
      <Spinner data-testid={TestIds.SPINNER} color={'primary'} size={'xl'} />
      Processing upload
      {type === EvidenceType.FILE && (
        <UploadInfoSection>
          <UploadInfo>
            {`${numFiles} file${numFiles > 1 ? 's' : ''}`}
          </UploadInfo>
          <UploadInfo>{`${uploadSize} MB`}</UploadInfo>
        </UploadInfoSection>
      )}
    </FilesLoadingContainer>
  )
}

export const AddEvidenceDetails = ({
  formData,
  formDataSet,
  formDataList,
  updateEvidence,
  updateEvidenceFiles,
  type,
  errors,
  updateUploadSize,
  modalId,
}: {
  formData: EvidenceFormData
  formDataSet: Dispatch<SetStateAction<EvidenceFormData>>
  formDataList: EvidenceFormData[]
  updateEvidence: (mime: MIME_TYPE, type: EvidenceType) => void
  updateEvidenceFiles: (files: EvidenceFormData[], type: EvidenceType) => void
  type: EvidenceType
  errors: { [key: string]: string }
  updateUploadSize: (totalSizeInMB: number) => void
  modalId: ModalFormId | undefined
}): JSX.Element => {
  const [list, setList] = useState<EvidenceFormData[]>(formDataList)
  useEffect(() => {
    setList(formDataList)
  }, [formDataList])
  return (
    <>
      <EvidenceDetailsContainer>
        <Label>Document Details</Label>
        {type === EvidenceType.LINK && (
          <P $color={palette.orange['900']} $isBold>
            Currently Trustero AI can only evaluate evidence that is stored in
            the platform. If you store your evidence somewhere else that you
            link to, Trustero AI will not be able to evaluate it.
          </P>
        )}
        <EvidenceTypeSelector
          updateEvidence={updateEvidence}
          updateEvidenceFiles={updateEvidenceFiles}
          type={type}
          updateUploadSize={updateUploadSize}
          modalId={modalId}
        />
        <EvidenceFiles formDataList={list} type={type} />
        <EvidenceText
          {...{
            formData,
            setFormData: formDataSet,
            formId: modalId ?? '',
            type,
          }}
        />
      </EvidenceDetailsContainer>
      <AddEvidenceError showError={!!errors.evidenceError}>
        {errors.evidenceError}
      </AddEvidenceError>
    </>
  )
}
